import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { ShieldCheck, ArrowClockwise, Bank } from "@phosphor-icons/react";
import Layout from "../components/Layout";
import api from "../lib/api";

const _MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const fmtDT = (v) => {
  if (!v) return "—";
  const m = String(v).match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/);
  return m ? `${m[3]}-${_MON[+m[2] - 1]}-${m[1].slice(2)} ${m[4]}:${m[5]}` : String(v).slice(0, 16);
};
const num = (v) => (Number(v) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const th = { padding: "9px 10px", fontSize: 11, fontWeight: 800, color: "#fff", whiteSpace: "nowrap", textAlign: "left" };
const td = { padding: "8px 10px", fontSize: 12.5, borderBottom: "1px solid #F1F5F9", whiteSpace: "nowrap" };

export default function SapPostAudit() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/fa-logistics/sap-post-audit");
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load SAP post audit");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const rows = data?.rows || [];

  return (
    <Layout>
      <div data-testid="sap-post-audit-page" style={{ padding: "22px 26px", maxWidth: 1180, margin: "0 auto" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
          <ShieldCheck size={26} weight="duotone" color="#0F766E" />
          <h1 style={{ fontSize: 22, fontWeight: 800, color: "#0F172A", margin: 0 }}>SAP Posts by This App</h1>
          <button data-testid="sap-audit-refresh" onClick={load} disabled={loading}
            style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 6, padding: "7px 13px",
              borderRadius: 9, border: "1px solid #CBD5E1", background: "#fff", fontSize: 12.5, fontWeight: 700,
              cursor: loading ? "default" : "pointer", color: "#0F172A" }}>
            <ArrowClockwise size={15} weight="bold" /> {loading ? "Loading…" : "Refresh"}
          </button>
        </div>
        <p style={{ fontSize: 13, color: "#64748B", margin: "0 0 18px" }}>
          The authoritative, complete record of every SAP bank-advice clearing <b>this application</b> has posted in
          this environment. If a SAP payment is not listed here, this app did not create it.
        </p>

        <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 18 }}>
          <div data-testid="sap-audit-count" style={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 12, padding: "14px 20px", minWidth: 160 }}>
            <div style={{ fontSize: 11, fontWeight: 800, color: "#94A3B8", textTransform: "uppercase", letterSpacing: 0.4 }}>App-posted clearings</div>
            <div style={{ fontSize: 26, fontWeight: 800, color: "#0F172A" }}>{data?.count ?? "—"}</div>
          </div>
          <div style={{ background: "#F0FDFA", border: "1px solid #99F6E4", borderRadius: 12, padding: "14px 20px", flex: 1, minWidth: 320 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 12.5, fontWeight: 800, color: "#0F766E", marginBottom: 4 }}>
              <Bank size={16} weight="fill" /> Reference format this app emits
            </div>
            <div style={{ fontSize: 13, color: "#134E4A" }}>
              Always <code style={{ background: "#CCFBF1", padding: "1px 6px", borderRadius: 5, fontWeight: 700 }}>BA&lt;invoice-digits&gt;</code> —
              the app <b>never</b> uses <code>CHK</code>, <code>PRB</code>, or a <code>…TDS</code> suffix. Those are manual SAP entries.
            </div>
          </div>
        </div>

        <div style={{ border: "1px solid #E2E8F0", borderRadius: 12, overflow: "hidden", background: "#fff" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#0F172A" }}>
                <th style={th}>Posted (UTC)</th>
                <th style={th}>By</th>
                <th style={th}>Sale No</th>
                <th style={th}>SAP Invoice</th>
                <th style={th}>External Ref</th>
                <th style={th}>Advice ID</th>
                <th style={th}>Co.</th>
                <th style={{ ...th, textAlign: "right" }}>Cleared</th>
                <th style={{ ...th, textAlign: "right" }}>Charges</th>
                <th style={th}>Ccy</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan={10} style={{ ...td, textAlign: "center", color: "#94A3B8", padding: "30px 0" }}>
                  {loading ? "Loading…" : "No SAP clearings have been posted by this app in this environment."}
                </td></tr>
              )}
              {rows.map((r, i) => (
                <tr key={i} data-testid={`sap-audit-row-${i}`}>
                  <td style={td}>{fmtDT(r.posted_at)}</td>
                  <td style={td}>{r.posted_by || "—"}</td>
                  <td style={td}>{r.sale_no || "—"}</td>
                  <td style={td}>{r.invoice_id || "—"}</td>
                  <td style={{ ...td, fontWeight: 700 }}>{r.external_id || "—"}</td>
                  <td style={td}>{r.advice_id || "—"}</td>
                  <td style={td}>{r.company || "—"}</td>
                  <td style={{ ...td, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{num(r.cleared_amount)}</td>
                  <td style={{ ...td, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{num(r.charges_total)}</td>
                  <td style={td}>{r.currency || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <p style={{ fontSize: 12, color: "#94A3B8", marginTop: 12 }}>
          Note: this shows posts logged by <b>this</b> environment only. To verify production, open this same page on the
          production app — its log is independent.
        </p>
      </div>
    </Layout>
  );
}
